import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { addMonths, endOfDay, format, isAfter, isBefore, parseISO, startOfDay } from 'date-fns';
import Formsy from 'formsy-react';
import { List } from 'immutable';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import HorizontalDateBox from '@sophocles/components/HorizontalDateBox';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import {
  fetchAnnualSolarReportsForSiteWithRange,
  getAnnualSolarReportsForSelectedStructure,
} from '@sophocles/entities/AnnualSolarReport';
import {
  fetchDailySolarReportsForSiteWithRange,
  getDailySolarReportsForSelectedStructure,
} from '@sophocles/entities/DailySolarReport';
import {
  fetchMonthlySolarReportsForSiteWithRange,
  getMonthlySolarReportsForSelectedStructure,
} from '@sophocles/entities/MonthlySolarReport';
import {
  fetchWeeklySolarReportsForSiteWithRange,
  getWeeklySolarReportsForSelectedStructure,
} from '@sophocles/entities/WeeklySolarReport';
import useDateRangeSearchParams from '@sophocles/hooks/useDateRangeSearchParams';
import PowerMenu from '@sophocles/views/sites/components/PowerMenu';
import SolarPowerChart from '@sophocles/views/sites/components/SolarPowerChart';
import SolarPowerList from '@sophocles/views/sites/components/SolarPowerList';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';

import SolarPowerGrid from './components/SolarPowerGrid';
import TicketLinksHeader from './components/TicketLinksHeader';

function toDate(value) {
  if (!value) return null;
  if (typeof value === 'string') return parseISO(value);
  if (value._isAMomentObject) return value.toDate();
  return value;
}

export function SolarPowerViewPage(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { user } = useUser();
  const { selectedStructure, loading: dashboardLoading } = useSiteDashboardLayoutContext();
  const selectedStructureId = params.site_id;
  const onMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const dailySolarReports = useSelector(getDailySolarReportsForSelectedStructure);
  const weeklySolarReports = useSelector(getWeeklySolarReportsForSelectedStructure);
  const monthlySolarReports = useSelector(getMonthlySolarReportsForSelectedStructure);
  const yearlySolarReports = useSelector(getAnnualSolarReportsForSelectedStructure);

  const [tab, setTab] = useState('daily');

  const defaultStart = useMemo(() => addMonths(new Date(), -2), []);
  const defaultEnd = useMemo(() => new Date(), []);
  const { startDate, endDate, onDateRangeChange } = useDateRangeSearchParams({
    defaultStart,
    defaultEnd,
  });

  useEffect(() => {
    const next = new URLSearchParams(location.search);
    let mutated = false;
    if (!next.get('date_start')) {
      next.set('date_start', format(startDate, 'yyyy-MM-dd'));
      mutated = true;
    }
    if (!next.get('date_end')) {
      next.set('date_end', format(endDate, 'yyyy-MM-dd'));
      mutated = true;
    }
    if (mutated) {
      navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
    }
  }, [location.pathname, location.search, startDate, endDate, navigate]);

  const fetchDataForTab = useCallback(
    (which, start, end) => {
      if (selectedStructureId == null) return;
      const from = format(start, 'yyyy-MM-dd');
      const to = format(end, 'yyyy-MM-dd');
      let action = null;
      if (which === 'daily') action = fetchDailySolarReportsForSiteWithRange(selectedStructureId, from, to);
      else if (which === 'weekly') action = fetchWeeklySolarReportsForSiteWithRange(selectedStructureId, from, to);
      else if (which === 'monthly') action = fetchMonthlySolarReportsForSiteWithRange(selectedStructureId, from, to);
      else if (which === 'yearly') action = fetchAnnualSolarReportsForSiteWithRange(selectedStructureId, from, to);
      if (!action) return;
      dispatch(action);
    },
    [dispatch, selectedStructureId],
  );

  useEffect(() => {
    fetchDataForTab(tab, startDate, endDate);
  }, [fetchDataForTab, tab, startDate, endDate]);

  const startDateFilter = useCallback(
    date => {
      if (typeof date !== 'string') onDateRangeChange(toDate(date), endDate);
    },
    [onDateRangeChange, endDate],
  );
  const endDateFilter = useCallback(
    date => {
      if (typeof date !== 'string') onDateRangeChange(startDate, toDate(date));
    },
    [onDateRangeChange, startDate],
  );

  if (dashboardLoading) {
    return <LoadingScreen />;
  }

  const isStaff = user.get('is_staff', false);
  const unitId = selectedStructure.getIn(['relay_controller', 'id'], 0);

  const startDateTime = startOfDay(startDate);
  const endDateTime = endOfDay(endDate);

  let dataSource = List();
  let chartTitle = '';
  if (tab === 'daily') {
    chartTitle = t('powerData.dailySolarReadings');
    dataSource = dailySolarReports;
  } else if (tab === 'weekly') {
    chartTitle = t('powerData.weeklySolarReadings');
    dataSource = weeklySolarReports;
  } else if (tab === 'monthly') {
    chartTitle = t('powerData.monthlySolarReadings');
    dataSource = monthlySolarReports;
  } else if (tab === 'yearly') {
    chartTitle = t('powerData.yearlySolarReadings');
    dataSource = yearlySolarReports;
  }

  const dataList = dataSource
    .filter(data => {
      const m = data.get('moment');
      return isAfter(m, startDateTime) && isBefore(m, endDateTime);
    })
    .map(data => {
      const generated = data.get('generated', 0);
      const consumed = data.get('consumed', 0);
      return data.merge({
        net: _.round(generated - consumed, 3),
        generated: _.round(generated, 3),
        consumed: _.round(consumed, 3),
        short: format(data.get('moment', new Date()), 'MM/dd'),
      });
    })
    .sortBy(item => item.get('moment'))
    .reverse();

  const plainList = dataList.toJS();
  _.reverse(plainList);

  const tabButtonSx = { margin: onMobile ? '4px 0 4px 3px' : '0 3px' };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        paddingBottom: 'var(--page-py)',
      }}
    >
      <TicketLinksHeader params={params} isStaff={isStaff} />
      {unitId != 0 ? (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: onMobile ? 'block' : 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Formsy className="form-inline">
              <HorizontalDateBox
                title={t('sites.startDate', 'Start Date')}
                name="start_date"
                value={startDate}
                labelClassName="no-bottom-margin pad-right"
                inputDivClassName="inline"
                inputClassName="form-control form-control-sm"
                outerClassName="form-group"
                onChange={startDateFilter}
                rightAlign={!!onMobile}
              />
              &nbsp; &nbsp;
              <HorizontalDateBox
                title={t('sites.endDate', 'End Date')}
                name="end_date"
                value={endDate}
                labelClassName="no-bottom-margin pad-right"
                inputDivClassName="inline"
                inputClassName="form-control form-control-sm"
                outerClassName="form-group"
                rightAlign
                onChange={endDateFilter}
                isValidDate={current => {
                  if (current?._isAMomentObject) return isBefore(current.toDate(), new Date());
                  return isBefore(current, new Date());
                }}
              />
            </Formsy>
            <Box sx={{ display: onMobile ? 'block' : 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                sx={tabButtonSx}
                color={tab === 'daily' ? 'primary' : 'secondary'}
                onClick={() => setTab('daily')}
              >
                {t('general.daily')}
              </Button>
              <Button
                variant="contained"
                sx={tabButtonSx}
                color={tab === 'weekly' ? 'primary' : 'secondary'}
                onClick={() => setTab('weekly')}
              >
                {t('general.weekly')}
              </Button>
              <Button
                variant="contained"
                sx={tabButtonSx}
                color={tab === 'monthly' ? 'primary' : 'secondary'}
                onClick={() => setTab('monthly')}
              >
                {t('general.monthly')}
              </Button>
              <Button
                variant="contained"
                sx={tabButtonSx}
                color={tab === 'yearly' ? 'primary' : 'secondary'}
                onClick={() => setTab('yearly')}
              >
                {t('general.yearly')}
              </Button>
              <PowerMenu selectedPage="solar" site={selectedStructure.toJS()} />
            </Box>
          </Box>
          <Typography variant="h6" align="center">
            {chartTitle}
          </Typography>
          <SolarPowerChart list={plainList} />
          <Box sx={{ height: 400, mt: 1 }}>
            {onMobile ? (
              <SolarPowerList unit_id={unitId} list={dataList} user={user} />
            ) : (
              <SolarPowerGrid list={dataList} />
            )}
          </Box>
        </Box>
      ) : (
        <Typography variant="h5" align="center" sx={{ mt: 4 }}>
          {t('powerData.noReadings')}
        </Typography>
      )}
    </Box>
  );
}

export default SolarPowerViewPage;
